import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { useApiClient } from 'app/data/api-client';
import { GetUserQueryResponse, User } from 'app/data/types/user';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { retryOnAuthError } from 'app/shared/auth';

export const USER_QUERY_KEY = 'user';

export const getUseUserQueryKey = (): QueryKey => [USER_QUERY_KEY];

/**
 * Fetches the currently authenticated user. Skipped when `sdkUser` is
 * already provided by the host frame's initResponse — that's the common
 * path.
 */
export const useUserQuery = (sdkUser?: User | null): GetUserQueryResponse => {
  const client = useApiClient();
  const { authToken } = useApi();
  const { addGenericError, hideGenericErrors } = useErrors();

  const { data, error, isLoading, isSuccess } = useQuery<User | null, Error>({
    queryKey: getUseUserQueryKey(),
    queryFn: async () => {
      const user = await client.get<User>('/api/users/current');
      return user ?? null;
    },
    enabled: !!authToken && !sdkUser,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      addGenericError(USER_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(USER_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    user: sdkUser ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
